/**
 * Component loader — the only script the theme loads eagerly.
 *
 * Architecture contract
 * ---------------------
 * Sections render complete, working HTML. Custom elements are progressive
 * enhancement on top of it: an un-upgraded <variant-picker> still renders real
 * radio inputs inside a real <form action="/cart/add">, so the page adds to
 * cart with JavaScript disabled, blocked, or still in flight.
 *
 * Nothing here blocks rendering, and no component module is fetched until it is
 * actually needed:
 *
 *   [data-eager]          imported as soon as the loader runs. Reserve this for
 *                         above-the-fold interactivity (header, buy block).
 *   near the viewport     IntersectionObserver with a 300px margin, so a module
 *                         is usually parsed before the user reaches it.
 *   first interaction     pointerdown / focusin / keydown in the capture phase.
 *
 * The interaction path is the important one for INP: a tap must never wait on an
 * observer callback to be scheduled. Capture phase means we start the import
 * before the event finishes propagating to the component's own listeners.
 *
 * Modules are keyed by tag name and imported at most once. The registry is
 * injected by snippets/component-scripts.liquid so every URL goes through
 * asset_url and gets Shopify's CDN and cache-busting.
 */

const registryElement = document.getElementById('component-registry');

/** @type {Record<string, string>} tag name -> module URL */
let registry = {};

try {
  registry = registryElement ? JSON.parse(registryElement.textContent) : {};
} catch (error) {
  console.error('[theme] component registry is not valid JSON', error);
}

const tags = Object.keys(registry);

if (tags.length > 0) {
  const selector = tags.join(',');

  /** Tags whose import has started. Value is the in-flight promise. */
  const started = new Map();

  /**
   * Import a component module once. Failures are logged and reset so a later
   * interaction can retry — a dropped chunk shouldn't permanently disable a
   * component for the rest of the session.
   */
  function upgrade(tag) {
    if (started.has(tag)) return started.get(tag);

    const url = registry[tag];
    if (!url) return undefined;

    const request = import(url).catch((error) => {
      started.delete(tag);
      console.error(`[theme] could not load component "${tag}"`, error);
    });

    started.set(tag, request);
    return request;
  }

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              observer.unobserve(entry.target);
              upgrade(entry.target.localName);
            }
          },
          { rootMargin: '300px' }
        )
      : null;

  /** Decide how a single element earns its module. */
  function watch(element) {
    const tag = element.localName;

    if (!registry[tag] || started.has(tag)) return;

    if (element.hasAttribute('data-eager') || !observer) {
      upgrade(tag);
      return;
    }

    /*
     * An element with no layout box can never intersect, so observing it would
     * mean its module never loads. That is the normal state for components that
     * decide their own visibility — the consent banner starts [hidden], the
     * recently-viewed list starts empty and hidden, the sticky buy bar sits
     * translated off-screen. Each of those has to run to know whether it should
     * appear at all, so they upgrade immediately.
     *
     * Checked here rather than requiring data-eager on each, because forgetting
     * it produces a component that silently never works — which is exactly the
     * bug this replaced.
     */
    const box = element.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) {
      upgrade(tag);
      return;
    }

    observer.observe(element);
  }

  /** Find every registered custom element inside a root and watch it. */
  function scan(root) {
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;

    if (root.matches?.(selector)) watch(root);

    for (const element of root.querySelectorAll(selector)) watch(element);
  }

  /**
   * Any interaction inside a registered element upgrades it immediately.
   * Capture phase, so the import is in flight before the component's own
   * handlers would have run.
   */
  function onInteraction(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const element = target.closest(selector);
    if (element) upgrade(element.localName);
  }

  for (const type of ['pointerdown', 'focusin', 'keydown']) {
    document.addEventListener(type, onInteraction, { capture: true, passive: true });
  }

  /**
   * Content that arrives after first paint: Section Rendering API responses,
   * cart drawer re-renders, paginated results.
   */
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) scan(node);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  /** Theme editor re-renders a section without a full page load. */
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  scan(document);
}
